import { agentDisplayLabel, agentNickname, agentRealName } from '../../utils/agentDisplay';
import { hasPermission } from '../../utils/permissions';
import { ADMIN_GROUPS, adminPagePath, visibleAdminPages } from './adminPages';

/**
 * What Admin search can find: every page, the settings inside them, the actions you can start,
 * and the people in the user list.
 *
 * A setting or action is one line here: the page it lives on, what it is called, and the words
 * people use for it. `anchor` is the name of an element on that page (data-anchor="...") that the
 * page scrolls to and flashes when a result is opened; `query` selects a tab on a page that has
 * them (?tool=resend). adminIndex.test.js reads the page files, so an anchor named here that no page
 * defines fails the tests instead of landing nowhere.
 */
const groupLabel = (id) => (ADMIN_GROUPS.find((group) => group.id === id) || {}).label || '';

// [page, kind, title, keywords, { anchor, query, requires }]; `requires` is a permission the page
// itself does not need but this one thing does.
const ITEMS = [
  // Statistics: who is counted
  ['stats-agent-roles', 'setting', 'Which Zendesk roles count in Statistics', ['who counts', 'cs agent', 'shift lead', 'senior', 'team average', 'role', 'counted'], { anchor: 'stats-agent-roles' }],
  // Quality reports: the numbers they use
  ['quality-rules', 'setting', 'Quality pass line and candidate mark', ['pass line', 'pass score', 'candidate', 'top performer', 'qa average', 'quality'], { anchor: 'quality-rules-pass' }],
  ['quality-rules', 'setting', 'Fewest and most scored tickets per agent', ['scored tickets', 'reviews', 'sample', 'insufficient', 'quality'], { anchor: 'quality-rules-sample' }],
  ['quality-rules', 'setting', 'Suggested 1x1 lines and planned sessions a month', ['1x1', 'weekly', 'biweekly', 'monthly', 'sessions', 'feedback', 'quality'], { anchor: 'quality-rules-tiers' }],
  ['quality-rules', 'setting', 'QA working pack thresholds', ['declining', 'improvement', 'positive progress', 'decline', 'audit mistake rate', 'working pack', 'quality'], { anchor: 'quality-rules-pack' }],
  // Alerts
  ['alerts', 'setting', 'Alert when still on Chats at slot time', ['last case', 'slack', 'break', 'slot'], { anchor: 'alerts-still-on-chats' }],
  ['alerts', 'setting', 'Alert when a break has not started after the grace time', ['grace', 'late', 'slack', 'not started'], { anchor: 'alerts-not-started' }],
  ['alerts', 'setting', 'Alert when a break is longer than the booked slot', ['over', 'grace', 'slack', 'duration'], { anchor: 'alerts-over-duration' }],
  ['alerts', 'setting', 'Alert when a booked break was missed', ['no break', 'slack', 'missed slot'], { anchor: 'alerts-missed-slot' }],
  ['alerts', 'setting', 'Over-duration alert only for booked slots', ['match window', 'booked'], { anchor: 'alerts-require-booked' }],
  ['alerts', 'setting', 'Email when someone stays on break for 2 hours or more', ['2h', 'two hours', 'meeting', 'training', 'long break'], { anchor: 'alerts-long-email' }],
  ['alerts', 'setting', 'Recipient of the 2 hour break email', ['email', 'to', 'who'], { anchor: 'alerts-long-recipient' }],
  ['alerts', 'setting', 'Threshold for the long break email (minutes)', ['120', 'minutes', '2h'], { anchor: 'alerts-long-threshold' }],
  ['alerts', 'setting', 'Minutes after the slot before "break not started"', ['grace', 'late'], { anchor: 'alerts-not-started-grace' }],
  ['alerts', 'setting', 'Match a break to its booked slot (plus or minus minutes)', ['match window', 'tolerance'], { anchor: 'alerts-slot-match' }],
  ['alerts', 'setting', 'Extra minutes on break before an over-duration alert', ['grace', 'over'], { anchor: 'alerts-over-grace' }],
  ['alerts', 'setting', 'Slot-start detection window (minutes)', ['poll', 'timing'], { anchor: 'alerts-slot-start-window' }],

  // Email reports
  ['email-reports', 'setting', 'Team report recipients (TL)', ['team lead', 'tl', 'blockbusters', 'royal flush', 'emails'], { anchor: 'email-teams' }],
  ['email-reports', 'setting', 'Head of CS report recipient', ['email', 'combined report'], { anchor: 'email-head-of-cs' }],
  ['email-reports', 'setting', 'Shift lead report recipients (SL)', ['sl', 'micro team', 'codes', 'emails'], { anchor: 'email-shift-leads' }],

  // Report tools (a tab each)
  ['report-tools', 'action', 'Send a manual test report', ['one-off', 'now', 'phm', 'email'], { query: 'tool=test' }],
  ['report-tools', 'action', 'Send a monthly test report', ['month', 'rollup', 'phm'], { query: 'tool=monthly' }],
  ['report-tools', 'action', 'Resend a corrected report', ['corrected', 'wrong data', 'again'], { query: 'tool=resend' }],
  ['report-tools', 'action', 'Flag a bad report day', ['block scheduled emails', 'placeholder', '9h', '11h', 'plain text notice', 'data wrong'], { query: 'tool=bad-day' }],

  // Google Sheets
  ['google-sheets', 'setting', 'Spreadsheet ID', ['grafix', 'sheet url', 'google sheet'], { anchor: 'sheets-id' }],
  ['google-sheets', 'setting', 'Sheet tab name', ['quarter', 'q1', 'q2', 'q3', 'q4', 'tab'], { anchor: 'sheets-tab' }],
  ['google-sheets', 'setting', 'Sync date range', ['from', 'to', 'dates'], { anchor: 'sheets-range' }],
  ['google-sheets', 'action', 'Sync the schedule from the sheet now', ['import', 'run', 'at work', 'roster'], { anchor: 'sheets-run' }],

  // Users
  ['users', 'action', 'Add a user', ['new', 'create', 'account', 'invite', 'login'], { query: 'new=1' }],
  ['users', 'setting', 'Change position, shift or team for many users', ['bulk', 'select', 'apply', 'several'], { anchor: 'users-bulk' }],

  // Positions and permissions
  ['positions', 'setting', 'Compare positions in the permission matrix', ['matrix', 'grants', 'access', 'who can open', 'rbac', 'differ'], {}],
  ['positions', 'setting', 'Read what one position can do, as a list', ['by position', 'what can they do', 'access', 'copy permissions'], { query: 'view=position' }],
  ['positions', 'setting', 'Rename or delete a position', ['roles', 'label'], { query: 'view=positions' }],
  ['positions', 'setting', 'Include a position on the Break Manager roster', ['roster', 'break manager', 'break scheduler', 'on roster'], { query: 'view=positions' }],
  ['positions', 'action', 'Add a position', ['new position', 'new role', 'access group'], { query: 'view=positions', anchor: 'add-position', requires: 'dossier.manage_catalogs' }],

  // Company Directory catalogs (a tab each)
  ['structure', 'setting', 'Departments', ['root', 'company chart', 'head position'], { query: 'catalog=departments' }],
  ['structure', 'setting', 'Teams', ['team colour', 'manager'], { query: 'catalog=teams' }],
  ['structure', 'setting', 'Positions and the org tree', ['reporting line', 'parent', 'org chart'], { query: 'catalog=positions' }],
  ['structure', 'setting', 'Employee statuses', ['active', 'inactive', 'resigned', 'released'], { query: 'catalog=statuses' }],
  ['pay-benefits', 'setting', 'Insurance base cap', ['ni', 'payroll', 'cap', 'social security'], { query: 'catalog=caps' }],
  ['pay-benefits', 'setting', 'Payroll rate sign-off', ['payroll', 'rates', 'approve'], { query: 'catalog=caps' }],
  ['pay-benefits', 'setting', 'Benefits list', ['assets', 'perks', 'night shift'], { query: 'catalog=benefits' }],
  ['pay-benefits', 'setting', 'Bonus types', ['bonuses'], { query: 'catalog=bonusTypes' }],
  ['checklists', 'setting', 'Onboarding checklist tasks', ['joiner', 'new starter', 'steps'], {}],
  ['checklists', 'setting', 'Offboarding checklist tasks', ['leaver', 'steps'], {}],

  // Desks
  ['desks', 'setting', 'Lock a desk so nobody can pick it', ['reserved', 'personal', 'locked', 'padlock', 'management', 'desk or home', 'not available'], { anchor: 'desks-table' }],
  ['desks', 'setting', 'Choose who may take a locked desk', ['assign', 'reserved for', 'people', 'owner', 'management team', 'personal desk'], { anchor: 'desks-table' }],
  ['desks', 'setting', 'Change a desk number or move it to another table', ['renumber', 'rename', 'position', 'order', 'floor plan', 'remove a desk'], { anchor: 'desks-table' }],
  ['desks', 'action', 'Add a desk or a table', ['new desk', 'new table', 'floor plan', 'seat', 'office'], { anchor: 'desks-add' }],

  // The rest
  ['schedule-notes', 'action', 'Approve, edit or dismiss a roster note', ['overtime', 'shift hours', 'review'], {}],
  ['google-calendar', 'action', 'Connect Google Calendar', ['oauth', 'sign in', 'shared account', 'reconnect'], {}],
  ['google-calendar', 'action', 'Check that a lead shares their calendar', ['shared', 'test', 'meetings'], {}],
  ['home-tools', 'action', 'Add a link to the home page', ['tile', 'tool', 'icon', 'group'], {}],
  ['stats-targets', 'setting', 'Minimum requirements for a month', ['kpi minimum requirements', 'minimum', 'tenure', 'new people', 'qa score', 'sat', 'emails per hour', 'chats per hour', 'monthly targets', 'colours', 'team', 'blockbusters', 'royal flush'], {}],
  ['stats-targets', 'setting', 'Minimum requirements in words', ['attendance', 'work ethic and attitude', 'shift hours', 'delays', 'minimum requirements', 'requirements text'], { query: 'tab=minimum' }],
  ['stats-targets', 'setting', 'Personal bonus requirements', ['bonus requirements', 'bonus', 'max monthly bonus', 'sum in eur', 'qa score', 'good surveys', 'gsr', 'paid leave'], { query: 'tab=bonus' }],
  ['stats-targets', 'setting', 'Agent of the Month', ['employee of the month', 'winner', 'reward', 'aotm'], { query: 'tab=eotm' }],
  ['stats-targets', 'setting', 'Quarterly team bonus', ['quarterly', 'team bonus', 'payout', 'q1', 'q2', 'q3', 'q4'], { query: 'tab=quarter' }]
];

const withQueryAndHash = (path, query, anchor) => `${path}${query ? `?${query}` : ''}${anchor ? `#${anchor}` : ''}`;

export const ADMIN_ITEMS = ITEMS.map(([page, kind, title, keywords, extra]) => ({
  page,
  kind,
  title,
  keywords,
  anchor: (extra && extra.anchor) || '',
  query: (extra && extra.query) || '',
  requires: (extra && extra.requires) || ''
}));

export function pageEntries(pages) {
  return pages.map((page) => ({
    id: `page:${page.id}`,
    kind: 'page',
    title: page.title,
    subtitle: groupLabel(page.group) || 'Daily work',
    description: page.description,
    keywords: page.keywords,
    to: adminPagePath(page),
    icon: page.icon
  }));
}

export function itemEntries(pages, user) {
  const byId = new Map(pages.map((page) => [page.id, page]));
  return ADMIN_ITEMS
    .filter((item) => byId.has(item.page))
    .filter((item) => !item.requires || hasPermission(user, item.requires))
    .map((item, index) => {
      const page = byId.get(item.page);
      return {
        id: `${item.kind}:${item.page}:${index}`,
        kind: item.kind,
        title: item.title,
        subtitle: page.title,
        // Only the item's own words: inheriting the page's made every setting on a page match
        // whatever one of them was about.
        description: '',
        keywords: item.keywords,
        to: withQueryAndHash(adminPagePath(page), item.query, item.anchor),
        icon: item.kind === 'action' ? 'action' : 'setting'
      };
    });
}

/** One entry per user, opening the Users page filtered to that person. */
export function personEntries(users, roles) {
  const labels = {};
  ((roles && roles.roles) || []).forEach((role) => { labels[role.slug] = role.label || role.slug; });
  return (users || []).map((user) => {
    const title = agentDisplayLabel(user);
    const position = labels[user.role] || user.role || '';
    const find = agentRealName(user) || user.email || title;
    return {
      id: `person:${user.id}`,
      kind: 'person',
      title,
      subtitle: [position, user.team].filter(Boolean).join(' · ') || 'User',
      description: user.email || '',
      keywords: [user.email, agentRealName(user), agentNickname(user), user.shift_lead, user.team, position].filter(Boolean),
      to: `/admin/users?q=${encodeURIComponent(find)}`,
      icon: 'person'
    };
  });
}

/** One entry per position, opening the list of everything that position can do. */
export function roleEntries(roles) {
  return ((roles && roles.roles) || []).map((role) => ({
    id: `role:${role.slug}`,
    kind: 'setting',
    title: `Permissions of ${role.label || role.slug}`,
    subtitle: 'Positions and permissions',
    description: '',
    keywords: [role.slug, role.label, 'position', 'role', 'access', 'what can they do'].filter(Boolean),
    to: `/admin/positions?view=position&role=${encodeURIComponent(role.slug)}`,
    icon: 'lock'
  }));
}

/** Every entry this user may open. `extra.users` and `extra.roles` add people and positions once they are loaded. */
export function buildAdminEntries(user, extra = {}) {
  const pages = visibleAdminPages(user);
  const entries = [...pageEntries(pages), ...itemEntries(pages, user)];
  if (pages.some((page) => page.id === 'users')) entries.push(...personEntries(extra.users, extra.roles));
  if (pages.some((page) => page.id === 'positions')) entries.push(...roleEntries(extra.roles));
  return entries;
}

